import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  setDoc,
  limit,
  onSnapshot,
  deleteField,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithEmailAndPassword,
  setPersistence, 
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { getApp, getApps, initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { db, auth, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import firebaseConfig from '../../firebase/config';
import { isCentralRole, isOperatorRole } from './authSlice';

const DEFAULT_ORGANIZATION_ID = process.env.REACT_APP_DEFAULT_ORGANIZATION_ID || 'default_organization';

const padDatePart = (value) => String(value).padStart(2, '0');

const formatLocalDateKey = (date) => (
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`
);

const getCurrentUser = (state) => state.auth?.user || null;

const getOperatorScope = (state) => {
  const currentUser = getCurrentUser(state);
  return isOperatorRole(currentUser) ? currentUser.operatorId || null : null;
};

const canManageDrivers = (user) => (
  isCentralRole(user) ||
  user?.role === 'operator_admin' ||
  user?.isAdmin === true
);

const assertOperatorAccess = (currentUser, data, entityName = 'Datensatz') => {
  if (isCentralRole(currentUser)) return;
  if (!isOperatorRole(currentUser) || !currentUser.operatorId) {
    throw new Error('Keine Berechtigung');
  }
  if (data.operatorId !== currentUser.operatorId) {
    throw new Error(`${entityName} gehoert zu einem anderen Unternehmer`);
  }
};

// Speicher für die letzte Aktualisierung und Daten
const driversCache = {
  lastFetched: null,
  data: [],
  fetchInProgress: false,
  cacheKey: null,
  // Cache-Zeit in Millisekunden (5 Minuten)
  cacheDuration: 5 * 60 * 1000
};

// Hilfsfunktion: Fahrt-basierten Status eines Fahrers bestimmen
// Gibt 'with_customer', 'en_route' oder 'paused' zurück
const getRideBasedStatus = async (userId) => {
  try {
    const snap = await getDocs(query(
      collection(db, 'rides'),
      where('driverId', '==', userId),
      where('status', 'in', ['accepted', 'arrived', 'in_progress'])
    ));
    if (snap.empty) return 'paused';
    const rideStatus = snap.docs[0].data().status;
    return rideStatus === 'in_progress' ? 'with_customer' : 'en_route';
  } catch {
    return 'paused';
  }
};

// Async thunks
export const fetchDrivers = createAsyncThunk(
  'drivers/fetchDrivers',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      const { forceRefresh = false, simplified = false } = options;
      const currentState = getState();
      const operatorScope = getOperatorScope(currentState);
      const cacheKey = operatorScope || 'central';
      const currentPath = window.location.pathname;
      const isDashboard = currentPath === '/dashboard';
      const isDriversPage = currentPath.startsWith('/fahrer');
      
      // Verhindert parallele Anfragen
      if (driversCache.fetchInProgress) {
        console.log('Bereits eine Anfrage in Bearbeitung, warte auf deren Abschluss...');
        return getState().drivers.drivers;
      }
      
      // Prüfe, ob die Fahrer bereits geladen wurden und der Cache gültig ist
      const cacheValid = driversCache.lastFetched && 
                         (Date.now() - driversCache.lastFetched < driversCache.cacheDuration);
      
      if (!forceRefresh && 
          cacheValid && 
          driversCache.cacheKey === cacheKey &&
          currentState.drivers.drivers && 
          currentState.drivers.drivers.length > 0) {
        console.log('Verwende gecachte Fahrerdaten (letzte Aktualisierung:', 
                   new Date(driversCache.lastFetched).toLocaleTimeString(), ')');
        return currentState.drivers.drivers;
      }
      
      console.log('Starte fetchDrivers...', { 
        forceRefresh, 
        simplified, 
        currentPath,
        isDashboard,
        isDriversPage
      });
      
      // Flag setzen, dass Anfrage läuft
      driversCache.fetchInProgress = true;
      
      // Änderung: Statt Driver-Sammlung werden Benutzer mit isDriver=true abgefragt
      // Nach isAvailable sortieren - verfügbare Fahrer zuerst
      const driversQuery = operatorScope
        ? query(
            collection(db, 'users'),
            where('isDriver', '==', true),
            where('operatorId', '==', operatorScope),
            orderBy('isAvailable', 'desc')
          )
        : query(
            collection(db, 'users'),
            where('isDriver', '==', true),
            orderBy('isAvailable', 'desc') // Verfügbare Fahrer (true) werden zuerst angezeigt
          );
      
      console.log('Führe Drivers-Query aus...');
      const querySnapshot = await getDocs(driversQuery);
      console.log('Query-Ergebnis:', querySnapshot.size, 'Fahrer gefunden');
      const drivers = [];
      
      // Parallele Verarbeitung für bessere Performance
      const driverPromises = querySnapshot.docs.map(async (userDoc) => {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Für Debug-Zwecke die ersten beiden Fahrer detailliert loggen
        const detailLogging = drivers.length < 2;
        if (detailLogging) {
          console.log('Verarbeite Fahrer:', userId);
          console.log('Fahrerdaten:', userData);
        }
        
        // Grundlegende Daten, die wir immer brauchen
        const driverBase = {
          id: userId,
          // Status basierend auf Online- und Verfügbarkeitsstatus
          status: userData.isOnline ? (userData.isAvailable ? 'available' : 'busy') : 'offline',
          rating: userData.rating || 0,
          ratingCount: userData.ratingCount || 0,
          total_rides: userData.total_rides || 0,
          isAvailable: userData.isAvailable || false,
          isOnline: userData.isOnline || false,
          organizationId: userData.organizationId || null,
          operatorId: userData.operatorId || null,
          user: {
            id: userId,
            name: userData.name,
            email: userData.email,
            phone: userData.phone || '',
            profile_picture: userData.profilePic || '',
            lastLogin: userData.lastLogin ? userData.lastLogin.toDate().toISOString() : null,
          },
          license_number: userData.licenseNumber || '',
          driverNumber: userData.driverNumber || '',
          license_expiry: userData.licenseExpiry || null,
          // Leere Platzhalter für erweiterte Daten
          location: null,
          ratings: [],
          vehicle: null
        };
        
        // Bei vereinfachter Ansicht oder wenn nicht auf Dashboard/Fahrerseite
        // geben wir die Basisdaten zurück
        if (simplified && !isDashboard && !isDriversPage) {
          return driverBase;
        }
        
        // Array für parallele Datenabfragen
        const dataFetchPromises = [];
        
        // Nur für Dashboard oder bei Direktzugriff auf Fahrerseite: Fahrzeugdaten laden
        if (userData.car && userData.car.vehicleId) {
          if (detailLogging) console.log('Suche Fahrzeug:', userData.car.vehicleId);
          
          const vehiclePromise = getDoc(doc(db, 'Vehicles', userData.car.vehicleId))
            .then(vehicleDoc => {
              if (vehicleDoc.exists()) {
                return {
                  id: vehicleDoc.id,
                  ...vehicleDoc.data()
                };
              }
              return null;
            })
            .catch(error => {
              console.warn('Fehler beim Abrufen des Fahrzeugs:', error);
              return null;
            });
            
          dataFetchPromises.push(vehiclePromise.then(data => { 
            driverBase.vehicle = data;
          }));
        }
        
        // Bewertungsdaten nur auf der Fahrerdetailseite laden
        if (isDriversPage && currentPath.includes(userId)) {
          if (detailLogging) console.log(`Hole Bewertungen für Fahrer ${userId}...`);
          
          const ratingsPromise = getDocs(query(
            collection(db, 'ratings'),
            where('driverId', '==', userId)
          ))
          .then(ratingsSnapshot => {
            return ratingsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          })
          .catch(error => {
            console.warn(`Fehler beim Abrufen der Bewertungen:`, error);
            return [];
          });
          
          dataFetchPromises.push(ratingsPromise.then(data => {
            driverBase.ratings = data;
          }));
        }
        
        // Standortdaten nur für Dashboard und LiveMap
        if (isDashboard) {
          if (detailLogging) console.log(`Hole Standort für Fahrer ${userId}...`);
          
          const locationPromise = getDoc(doc(db, 'driverLocations', userId))
            .then(locationDoc => {
              if (locationDoc.exists()) {
                const locData = locationDoc.data();
                return {
                  latitude: locData.latitude,
                  longitude: locData.longitude
                };
              }
              return null;
            })
            .catch(error => {
              console.warn(`Fehler beim Abrufen des Standorts:`, error);
              return null;
            });
            
          dataFetchPromises.push(locationPromise.then(data => {
            driverBase.location = data;
          }));
        }
        
        // Status bestimmen basierend auf isOnline, isAvailable und aktivem Fahrt-Status
        if (userData.isOnline === true) {
          if (userData.isAvailable === true) {
            driverBase.status = 'available';
          } else {
            // Online aber nicht verfügbar → Fahrt prüfen für Detailstatus
            dataFetchPromises.push(
              getRideBasedStatus(userId).then(s => { driverBase.status = s; })
            );
          }
        } else {
          driverBase.status = 'offline';
        }
        
        // Fahrtenzählung für Dashboard oder Fahrerseite
        if (isDashboard || (isDriversPage && currentPath.includes(userId))) {
          const ridesPromise = getDocs(query(
            collection(db, 'rides'),
            where('driverId', '==', userId),
            limit(100) // Limit für bessere Performance
          ))
          .then(ridesSnapshot => {
            return ridesSnapshot.docs.length;
          })
          .catch(error => {
            console.warn(`Fehler beim Zählen der Fahrten:`, error);
            return userData.total_rides || 0;
          });
          
          dataFetchPromises.push(ridesPromise.then(count => {
            driverBase.total_rides = count;
          }));
        }
        
        // Alle Daten parallel laden und dann den Fahrer zurückgeben
        if (dataFetchPromises.length > 0) {
          await Promise.all(dataFetchPromises);
        }
        
        return driverBase;
      });
      
      // Alle Fahrer-Promises parallel ausführen
      const processedDrivers = await Promise.all(driverPromises);
      drivers.push(...processedDrivers);
      
      console.log('Fahrerverarbeitung abgeschlossen, gefundene Fahrer:', drivers.length);
      
      // Cache aktualisieren
      driversCache.data = drivers;
      driversCache.lastFetched = Date.now();
      driversCache.cacheKey = cacheKey;
      driversCache.fetchInProgress = false;
      
      return drivers;
    } catch (error) {
      console.error('Error fetching drivers:', error);
      // Vollständigen Fehler zurückgeben für bessere Diagnose
      return rejectWithValue({
        message: error.message,
        code: error.code,
        stack: error.stack,
        details: 'Fehler beim Abrufen der Fahrer. Bitte prüfen Sie die Konsole für Details.'
      });
    } finally {
      // Sicherstellen, dass fetchInProgress immer zurückgesetzt wird
      driversCache.fetchInProgress = false;
      console.log('driversCache.fetchInProgress auf false gesetzt (finally)');
    }
  }
);

export const fetchDriverById = createAsyncThunk(
  'drivers/fetchDriverById',
  async (driverId, { getState, rejectWithValue }) => {
    try {
      console.log('Fetching driver by ID:', driverId);
      
      // Änderung: Fahrer wird direkt aus users Sammlung abgefragt
      const userDoc = await getDoc(doc(db, 'users', driverId));
      
      if (!userDoc.exists()) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      const userData = userDoc.data();
      
      // Prüfen, ob es sich wirklich um einen Fahrer handelt
      if (!userData.isDriver) {
        throw new Error('Benutzer ist kein Fahrer');
      }

      assertOperatorAccess(getCurrentUser(getState()), userData, 'Fahrer');
      
      // Fahrzeugdaten holen, falls vorhanden
      let vehicleData = null;
      if (userData.car && userData.car.vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', userData.car.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = {
            id: vehicleDoc.id,
            ...vehicleDoc.data()
          };
        }
      }
      
      // Bewertungsdaten aggregieren
      const ratingsQuery = query(
        collection(db, 'ratings'),
        where('driverId', '==', driverId)
      );
      
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const ratings = ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Fahrerstandort holen
      let locationData = null;
      const locationDoc = await getDoc(doc(db, 'driverLocations', driverId));
      if (locationDoc.exists()) {
        locationData = locationDoc.data();
      }
      
      // Fahrten zählen
      const ridesQuery = query(
        collection(db, 'rides'),
        where('driverId', '==', driverId)
      );
      const ridesSnapshot = await getDocs(ridesQuery);
      const totalRides = ridesSnapshot.docs.length;
      
      // Fahrerstatus bestimmen basierend auf isOnline, isAvailable und aktivem Fahrt-Status
      let status;

      if (!userData.isOnline) {
        status = 'offline';
      } else if (userData.isAvailable) {
        status = 'available';
      } else {
        status = await getRideBasedStatus(driverId);
      }
      
      return {
        id: driverId,
        status,
        rating: userData.rating || 0,
        ratingCount: userData.ratingCount || 0,
        total_rides: totalRides,
        // Verwende isAvailable für Aktivitätsstatus
        isAvailable: userData.isAvailable || false,
        isOnline: userData.isOnline || false,
        organizationId: userData.organizationId || null,
        operatorId: userData.operatorId || null,
        location: locationData ? {
          latitude: locationData.latitude,
          longitude: locationData.longitude
        } : null,
        ratings,
        user: {
          id: driverId,
          name: userData.name,
          email: userData.email,
          phone: userData.phone || '',
          profile_picture: userData.profilePic || '',
          lastLogin: userData.lastLogin ? userData.lastLogin.toDate().toISOString() : null,
        },
        vehicle: vehicleData,
        // Zusätzliche Fahrerinfos, falls vorhanden (für Erweiterungen)
        license_number: userData.licenseNumber || '',
        driverNumber: userData.driverNumber || '',
        license_expiry: userData.licenseExpiry ? userData.licenseExpiry.toDate().toISOString() : null,
      };
    } catch (error) {
      console.error('Error fetching driver by ID:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Funktion, die in DriverForm.js verwendet wird
export const createDriver = createAsyncThunk(
  'drivers/createDriver',
  async (driverData, { rejectWithValue, dispatch, getState }) => {
    try {
      const { 
        name, 
        email, 
        phone, 
        password,
        driverNumber,
        license_number, 
        license_expiry, 
        documents 
      } = driverData;
      
      // Zuerst die aktuellen Admin-Anmeldeinformationen speichern
      const { auth: authState } = getState();
      const adminUser = authState.user;
      
      if (!canManageDrivers(adminUser)) {
        throw new Error('Sie benötigen Administrator-Rechte, um Fahrer zu erstellen');
      }

      const operatorId = isOperatorRole(adminUser)
        ? adminUser.operatorId
        : (driverData.operatorId || null);

      if (isOperatorRole(adminUser) && !operatorId) {
        throw new Error('Ihr Konto ist keinem Unternehmer zugeordnet');
      }

      const normalizedDriverNumber = (driverNumber || '').trim();
      if (normalizedDriverNumber) {
        const numberSnapshot = await getDocs(query(
          collection(db, 'users'),
          where('isDriver', '==', true),
          where('driverNumber', '==', normalizedDriverNumber)
        ));
        if (!numberSnapshot.empty) {
          throw new Error(`Fahrernummer ${normalizedDriverNumber} ist bereits vergeben`);
        }
      }
      
      console.log('Admin creating new driver:', adminUser.email);
      console.log('Creating driver with auth credentials:', { email });
      
      // Sichern der Admin-Credentials für späteren Relogin
      const adminEmail = adminUser.email;
      
      // WICHTIG: Wir erstellen eine zweite, separate Firebase-App-Instanz nur für diese Operation
      // Dadurch wird die Hauptsitzung nicht beeinflusst
      const secondaryApp = getApps().some(app => app.name === 'secondary')
        ? getApp('secondary')
        : initializeApp(firebaseConfig, 'secondary');
      const secondaryAuth = getAuth(secondaryApp);
      
      // Benutzer in Firebase Authentication erstellen mit der sekundären Auth-Instanz
      console.log('Creating user with secondary auth instance...');
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
      const user = userCredential.user;
      
      // Benutzerprofile aktualisieren
      await updateProfile(user, {
        displayName: name
      });
      
      const uid = user.uid;
      console.log('Firebase Auth user created with ID:', uid);
      
      // Sofort abmelden vom sekundären Auth-Objekt
      await secondaryAuth.signOut();
      
      console.log('Secondary auth instance signed out, admin session preserved');
      
      try {
        // Neuen Benutzer in users-Sammlung erstellen
        const userData = {
          name,
          email,
          phone,
          role: 'driver',
          organizationId: adminUser.organizationId || DEFAULT_ORGANIZATION_ID,
          operatorId,
          isDriver: true,         // Laut DB-Dokumentation: Fahrer werden durch isDriver=true markiert
          isAvailable: false,     // Initial nicht verfügbar
          rating: 5.0,            // Standardbewertung für neue Fahrer: 5.0 Sterne
          ratingCount: 0,
          // Fahrerdetails
          driverNumber: normalizedDriverNumber,
          licenseNumber: license_number || '',
          licenseExpiry: license_expiry ? Timestamp.fromDate(new Date(license_expiry)) : null,
          // Metadaten
          createdAt: Timestamp.now(),
          createdBy: adminUser.uid,
          createdByRole: adminUser.role || 'admin',
          lastLogin: Timestamp.now(),
          platform: 'web',
          profilePic: '',
          // Keine 'role' für Fahrer nötig, da isDriver=true der Identifikator ist
          // Passwort nicht mehr speichern, da es jetzt im Auth-System ist
        };
        
        console.log('Creating user document in Firestore...');
        
        // Benutzer in Firestore speichern
        await setDoc(doc(db, 'users', uid), userData);
        
        console.log('Creating driver location document...');
        
        // Standortdokument für den Fahrer erstellen (leer)
        await setDoc(doc(db, 'driverLocations', uid), {
          latitude: 0,
          longitude: 0,
          heading: 0,
          speed: 0,
          orderId: "",
          organizationId: adminUser.organizationId || DEFAULT_ORGANIZATION_ID,
          operatorId,
          timestamp: Timestamp.now()
        });

        console.log('Successfully created Firestore documents');
      } catch (firestoreError) {
        console.error('Error creating Firestore documents:', firestoreError);
        throw firestoreError;
      }
      
      console.log('Driver creation successful, fetching driver data...');
      
      // Treiberdaten abrufen - verwenden Sie die lokale uid-Variable
      const newDriverData = {
        id: uid,
        status: 'offline',
        name,
        email,
        phone,
        driverNumber: normalizedDriverNumber,
        license_number,
        license_expiry,
        // Der Rest kann aus den anderen Daten abgeleitet werden
      };
      
      return newDriverData;
    } catch (error) {
      console.error('Error creating driver:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateDriver = createAsyncThunk(
  'drivers/updateDriver',
  async ({ id, ...driverData }, { getState, rejectWithValue, dispatch }) => {
    try {
      // Änderung: Direkte Aktualisierung des Benutzerdokuments
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      const userData = userDoc.data();
      
      // Prüfen, ob es sich wirklich um einen Fahrer handelt
      if (!userData.isDriver) {
        throw new Error('Benutzer ist kein Fahrer');
      }

      assertOperatorAccess(getCurrentUser(getState()), userData, 'Fahrer');
      
      // Daten für das Benutzerdokument vorbereiten
      const updateData = {};
      
      if (driverData.user) {
        if (driverData.user.name) updateData.name = driverData.user.name;
        if (driverData.user.phone) updateData.phone = driverData.user.phone;
        if (driverData.user.profile_picture) updateData.profilePic = driverData.user.profile_picture;
      }
      
      if (driverData.driverNumber !== undefined) {
        const normalizedDriverNumber = (driverData.driverNumber || '').trim();
        if (normalizedDriverNumber) {
          const numberSnapshot = await getDocs(query(
            collection(db, 'users'),
            where('isDriver', '==', true),
            where('driverNumber', '==', normalizedDriverNumber)
          ));
          const duplicate = numberSnapshot.docs.find(docSnapshot => docSnapshot.id !== id);
          if (duplicate) {
            throw new Error(`Fahrernummer ${normalizedDriverNumber} ist bereits vergeben`);
          }
        }
        updateData.driverNumber = normalizedDriverNumber;
      }
      if (driverData.license_number) updateData.licenseNumber = driverData.license_number;
      if (driverData.license_expiry) updateData.licenseExpiry = Timestamp.fromDate(new Date(driverData.license_expiry));
      
      // User-Dokument aktualisieren
      await updateDoc(userRef, {
        ...updateData,
        updated_at: Timestamp.now()
      });

      // Aktualisierte Daten direkt aus Firestore laden
      const updatedDoc = await getDoc(userRef);
      const updatedData = updatedDoc.data();

      return {
        id,
        status: userData.isOnline ? (userData.isAvailable ? 'available' : 'busy') : 'offline',
        rating: updatedData.rating || 0,
        ratingCount: updatedData.ratingCount || 0,
        total_rides: updatedData.total_rides || 0,
        isAvailable: updatedData.isAvailable || false,
        isOnline: updatedData.isOnline || false,
        organizationId: updatedData.organizationId || null,
        operatorId: updatedData.operatorId || null,
        location: null,
        ratings: [],
        user: {
          id,
          name: updatedData.name,
          email: updatedData.email,
          phone: updatedData.phone || '',
          profile_picture: updatedData.profilePic || '',
          lastLogin: updatedData.lastLogin ? updatedData.lastLogin.toDate().toISOString() : null,
        },
        vehicle: null,
        driverNumber: updatedData.driverNumber || '',
        license_number: updatedData.licenseNumber || '',
        license_expiry: updatedData.licenseExpiry ? updatedData.licenseExpiry.toDate().toISOString() : null,
      };
    } catch (error) {
      console.error('Error updating driver:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const toggleDriverActive = createAsyncThunk(
  'drivers/toggleDriverActive',
  async ({ id, isActive }, { getState, rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', id);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      assertOperatorAccess(getCurrentUser(getState()), userDoc.data(), 'Fahrer');
      await updateDoc(userRef, { isActive, updated_at: Timestamp.now() });
      return { id, isActive };
    } catch (error) {
      console.error('Fehler beim Ändern des Aktivierungsstatus:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteDriver = createAsyncThunk(
  'drivers/deleteDriver',
  async (id, { rejectWithValue }) => {
    try {
      console.log('Rufe Cloud Function deleteDriver auf für ID:', id);
      const deleteDriverFn = httpsCallable(functions, 'deleteDriver');
      await deleteDriverFn({ driverId: id });
      console.log('Fahrer erfolgreich gelöscht:', id);
      return id;
    } catch (error) {
      console.error('Fehler beim Löschen des Fahrers:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const assignVehicleToDriver = createAsyncThunk(
  'drivers/assignVehicle',
  async ({ driverId, vehicleId }, { getState, rejectWithValue, dispatch }) => {
    try {
      // Fahrzeug abrufen
      const vehicleDoc = await getDoc(doc(db, 'Vehicles', vehicleId));
      if (!vehicleDoc.exists()) {
        throw new Error('Fahrzeug nicht gefunden');
      }
      const vehicleData = vehicleDoc.data();
      assertOperatorAccess(getCurrentUser(getState()), vehicleData, 'Fahrzeug');
      
      // Fahrer abrufen
      const userRef = doc(db, 'users', driverId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      assertOperatorAccess(getCurrentUser(getState()), userDoc.data(), 'Fahrer');

      if ((vehicleData.operatorId || null) !== (userDoc.data().operatorId || null)) {
        throw new Error('Fahrer und Fahrzeug muessen zum selben Unternehmer gehoeren');
      }
      
      // Fahrzeug dem Fahrer zuweisen
      await updateDoc(userRef, {
        car: {
          vehicleId: vehicleId,
          model: vehicleData.model,
          licensePlate: vehicleData.licensePlate
        },
        updated_at: Timestamp.now()
      });
      
      // Fahrer als aktuellen Fahrer des Fahrzeugs eintragen
      await updateDoc(doc(db, 'Vehicles', vehicleId), {
        driverId: driverId,
        inUse: true,
        lastActivityTimestamp: Timestamp.now()
      });
      
      // Aktualisierte Daten abrufen
      return await fetchDriverById(driverId)(dispatch);
    } catch (error) {
      console.error('Error assigning vehicle to driver:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  drivers: [],
  currentDriver: null,
  isLoading: false,
  error: null
};

// Aktiver Listener-Speicher außerhalb des Reducer, um Cleanup zu ermöglichen
let driversStatusListener = null;

const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    clearDriversError: (state) => {
      state.error = null;
    },
    clearCurrentDriver: (state) => {
      state.currentDriver = null;
    },
    updateDriverRealtime: (state, action) => {
      const driverData = action.payload;
      const index = state.drivers.findIndex(driver => driver.id === driverData.id);
      
      if (index !== -1) {
        // Nur die aktualisierten Felder übernehmen
        state.drivers[index] = {
          ...state.drivers[index],
          ...driverData
        };
        
        // Debug-Ausgabe für Aktualisierungen
        console.log(`Driver ${driverData.id} updated in Redux store: `, {
          isOnline: state.drivers[index].isOnline,
          isAvailable: state.drivers[index].isAvailable,
          status: state.drivers[index].status
        });
      } else {
        console.warn(`Driver with ID ${driverData.id} not found in drivers array`);
      }
      
      // Auch den aktuellen Fahrer aktualisieren, falls es derselbe ist
      if (state.currentDriver && state.currentDriver.id === driverData.id) {
        state.currentDriver = {
          ...state.currentDriver,
          ...driverData
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all drivers
      .addCase(fetchDrivers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.drivers = action.payload;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch driver by ID
      .addCase(fetchDriverById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDriverById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDriver = action.payload;
      })
      .addCase(fetchDriverById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create driver
      .addCase(createDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.drivers.push(action.payload);
        state.currentDriver = action.payload;
      })
      .addCase(createDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update driver
      .addCase(updateDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.drivers.findIndex(driver => driver.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.currentDriver && state.currentDriver.id === action.payload.id) {
          state.currentDriver = action.payload;
        }
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete driver
      .addCase(deleteDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        state.drivers = state.drivers.filter(driver => driver.id !== action.payload);
        if (state.currentDriver && state.currentDriver.id === action.payload) {
          state.currentDriver = null;
        }
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Assign vehicle to driver
      .addCase(assignVehicleToDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignVehicleToDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.drivers.findIndex(driver => driver.id === action.payload.id);
        if (index !== -1) {
          state.drivers[index] = action.payload;
        }
        if (state.currentDriver && state.currentDriver.id === action.payload.id) {
          state.currentDriver = action.payload;
        }
      })
      .addCase(assignVehicleToDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle driver active
      .addCase(toggleDriverActive.fulfilled, (state, action) => {
        const { id, isActive } = action.payload;
        const index = state.drivers.findIndex(driver => driver.id === id);
        if (index !== -1) {
          state.drivers[index] = { ...state.drivers[index], isActive };
        }
        if (state.currentDriver && state.currentDriver.id === id) {
          state.currentDriver = { ...state.currentDriver, isActive };
        }
      })
      // Logout driver
      .addCase(logoutDriver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logoutDriver.fulfilled, (state, action) => {
        state.isLoading = false;
        const driverId = action.payload;
        const index = state.drivers.findIndex(driver => driver.id === driverId);
        if (index !== -1) {
          // Nur das car-Feld aktualisieren, nicht den gesamten Fahrer ersetzen
          state.drivers[index] = {
            ...state.drivers[index],
            vehicle: null
          };
        }
        if (state.currentDriver && state.currentDriver.id === driverId) {
          state.currentDriver = {
            ...state.currentDriver,
            vehicle: null
          };
        }
      })
      .addCase(logoutDriver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleDriverAvailability.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleDriverAvailability.fulfilled, (state, action) => {
        state.isLoading = false;
        const { driverId, isAvailable } = action.payload;
        const index = state.drivers.findIndex(driver => driver.id === driverId);
        if (index !== -1) {
          state.drivers[index] = {
            ...state.drivers[index],
            isAvailable
          };
        }
        if (state.currentDriver && state.currentDriver.id === driverId) {
          state.currentDriver = {
            ...state.currentDriver,
            isAvailable
          };
        }
      })
      .addCase(toggleDriverAvailability.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  }
});

export const logoutDriver = createAsyncThunk(
  'drivers/logoutDriver',
  async (driverId, { rejectWithValue }) => {
    try {
      // Fahrer abrufen
      const userRef = doc(db, 'users', driverId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      
      const userData = userDoc.data();
      
      // Prüfen, ob der Fahrer ein Fahrzeug zugewiesen hat
      if (userData.car && userData.car.vehicleId) {
        const vehicleId = userData.car.vehicleId;
        const vehicleRef = doc(db, 'Vehicles', vehicleId);
        const vehicleDoc = await getDoc(vehicleRef);
        
        if (vehicleDoc.exists()) {
          // Fahrzeug freigeben
          await updateDoc(vehicleRef, {
            inUse: false,
            driverId: null,
            lastActivityTimestamp: Timestamp.now()
          });
        }
      }
      
      // Remote-Logout für den Fahrer aktivieren und Fahrzeugdaten entfernen
      await updateDoc(userRef, {
        car: null,
        forceLogout: true,
        forceLogoutTimestamp: Timestamp.now(),
        lastLogoutCheck: null,
        isOnline: false,
        isAvailable: false,
      });

      // driverLocations ebenfalls aktualisieren (für Dispatcher-Anzeige und Wartezeitberechnung)
      try {
        const driverLocRef = doc(db, 'driverLocations', driverId);
        const driverLocSnap = await getDoc(driverLocRef);
        if (driverLocSnap.exists()) {
          await updateDoc(driverLocRef, { isAvailable: false });
        }
      } catch (locErr) {
        console.warn('driverLocations Update fehlgeschlagen:', locErr);
      }

      return driverId;
    } catch (error) {
      console.error('Error logging out driver:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Thunk zum Umschalten der Fahrer-Verfügbarkeit (Disponent → Mobile App)
export const toggleDriverAvailability = createAsyncThunk(
  'drivers/toggleDriverAvailability',
  async ({ driverId, setAvailable, pauseType, pauseDuration }, { rejectWithValue }) => {
    try {
      const userRef = doc(db, 'users', driverId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }

      // 1. shifts-Collection: Schicht/Pause synchronisieren — MUSS vor dem
      // user-doc-Write (Schritt 2) laufen, damit die Mobile-App beim Empfang
      // von forceAvailabilityChange die Schicht bereits vorfindet und nur
      // reaktiviert (kein Race, keine doppelte aktive Schicht).
      // Damit Wartezeitberechnung und Taxi-Kalender sofort aktuell sind.
      // Schicht kann über Mitternacht laufen → heute UND gestern prüfen (wie Mobile App)
      try {
        const today = new Date();

        // handled = aktive Schicht gefunden (Pause gesetzt/beendet) ODER
        //           kürzlich beendete Schicht reaktiviert.
        let handled = false;

        for (let dayOffset = 0; dayOffset <= 1 && !handled; dayOffset++) {
          const checkDate = new Date(today);
          checkDate.setDate(checkDate.getDate() - dayOffset);
          const dateStr = formatLocalDateKey(checkDate);
          const shiftsRef = doc(db, 'shifts', dateStr);

          handled = await runTransaction(db, async (transaction) => {
            const shiftsSnap = await transaction.get(shiftsRef);
            if (!shiftsSnap.exists()) return false;

            const data = shiftsSnap.data();
            const driverData = data.drivers?.[driverId];
            if (!driverData?.shifts?.length) return false;

            const shifts = [...driverData.shifts];
            // Aktiven Shift finden (letzter mit isActive === true)
            const activeIndex = shifts.findLastIndex(s => s.isActive);

            if (activeIndex !== -1) {
              // Aktive Schicht vorhanden → Pause setzen/beenden
              const breaks = [...(shifts[activeIndex].breaks || [])];

              if (!setAvailable) {
                // Break-Eintrag hinzufügen
                const now = Timestamp.now();
                breaks.push({
                  startTime: now,
                  type: pauseType,
                  plannedDuration: pauseDuration ?? null,
                  endTime: pauseDuration
                    ? Timestamp.fromMillis(now.toMillis() + pauseDuration * 60 * 1000)
                    : null,
                });
              } else {
                // Letzten offenen Break frühzeitig beenden
                for (let i = breaks.length - 1; i >= 0; i--) {
                  const b = breaks[i];
                  const endTime = b.endTime?.toMillis?.() ?? null;
                  if (endTime === null || endTime > Date.now()) {
                    breaks[i] = { ...b, endTime: Timestamp.now() };
                    break;
                  }
                }
              }

              shifts[activeIndex] = { ...shifts[activeIndex], breaks };
              transaction.update(shiftsRef, {
                [`drivers.${driverId}.shifts`]: shifts,
              });
              return true;
            }

            // Keine aktive Schicht. Nur beim Verfügbar-Setzen relevant:
            // Letzte Schicht reaktivieren, wenn sie < 12h alt ist (analog
            // Mobile-App _shouldStartNewShift). Ohne aktiven Schicht-Eintrag
            // rechnet die Wartezeitberechnung den Fahrer sonst nicht mit.
            if (setAvailable) {
              const lastIndex = shifts.length - 1;
              const lastShift = shifts[lastIndex];
              const startMs = lastShift?.shiftStart?.toMillis?.() ?? null;
              if (startMs !== null && Date.now() - startMs < 12 * 60 * 60 * 1000) {
                const reactivated = { ...lastShift, isActive: true };
                delete reactivated.shiftEnd;
                if (Array.isArray(reactivated.offlinePeriods)) {
                  reactivated.offlinePeriods = reactivated.offlinePeriods.map((op) =>
                    op && op.offlineEnd == null
                      ? { ...op, offlineEnd: Timestamp.now() }
                      : op
                  );
                }
                shifts[lastIndex] = reactivated;
                transaction.update(shiftsRef, {
                  [`drivers.${driverId}.shifts`]: shifts,
                });
                return true;
              }
            }

            return false;
          });
        }

        // Kein aktiver/reaktivierbarer Schicht-Eintrag, aber Fahrer wird
        // verfügbar gesetzt → neue Schicht für heute anlegen, damit die
        // Wartezeitberechnung den Fahrer sieht (Symmetrie zur Mobile-App
        // _startShift). Sonst: Fahrer verfügbar, aber "Keine Fahrer verfügbar".
        if (setAvailable && !handled) {
          const userData = userDoc.data();
          const dateStr = formatLocalDateKey(today);
          const shiftsRef = doc(db, 'shifts', dateStr);

          await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(shiftsRef);
            const orgId = userData.organizationId || null;
            const opId = userData.operatorId || null;
            const driverName = userData.name || '';

            const buildShift = (shiftNumber) => ({
              shiftNumber,
              shiftStart: Timestamp.now(),
              shiftEnd: null,
              isActive: true,
              ...(orgId ? { organizationId: orgId } : {}),
              ...(opId ? { operatorId: opId } : {}),
              breaks: [],
              offlinePeriods: [],
            });

            if (!snap.exists()) {
              transaction.set(shiftsRef, {
                date: dateStr,
                drivers: {
                  [driverId]: {
                    driverId,
                    driverName,
                    ...(orgId ? { organizationId: orgId } : {}),
                    ...(opId ? { operatorId: opId } : {}),
                    shifts: [buildShift(1)],
                    currentShiftNumber: 1,
                    totalShifts: 1,
                  },
                },
              });
              return;
            }

            const existing = snap.data().drivers?.[driverId];
            if (!existing?.shifts?.length) {
              transaction.update(shiftsRef, {
                [`drivers.${driverId}.driverId`]: driverId,
                [`drivers.${driverId}.driverName`]: driverName,
                ...(orgId ? { [`drivers.${driverId}.organizationId`]: orgId } : {}),
                ...(opId ? { [`drivers.${driverId}.operatorId`]: opId } : {}),
                [`drivers.${driverId}.shifts`]: [buildShift(1)],
                [`drivers.${driverId}.currentShiftNumber`]: 1,
                [`drivers.${driverId}.totalShifts`]: 1,
              });
              return;
            }

            // Fahrer hat heute Einträge, aber keine aktive/reaktivierbare
            // Schicht → neue Schicht anhängen.
            const totalShifts = (existing.totalShifts ?? existing.shifts.length) + 1;
            const updatedShifts = [...existing.shifts, buildShift(totalShifts)];
            transaction.update(shiftsRef, {
              [`drivers.${driverId}.shifts`]: updatedShifts,
              [`drivers.${driverId}.currentShiftNumber`]: totalShifts,
              [`drivers.${driverId}.totalShifts`]: totalShifts,
            });
          });
        }
      } catch (shiftErr) {
        console.warn('shifts Sync fehlgeschlagen:', shiftErr);
      }

      // 2. User-Dokument aktualisieren (+ Signal für Mobile App)
      //    Erst NACH dem shift-Sync, damit die Mobile-App die Schicht beim
      //    Empfang von forceAvailabilityChange bereits vorfindet.
      await updateDoc(userRef, {
        isAvailable: setAvailable,
        forceAvailabilityChange: setAvailable ? 'available' : 'busy',
        forceAvailabilityTimestamp: Timestamp.now(),
        forceAvailabilityPauseType: setAvailable ? null : pauseType,
        forceAvailabilityPauseDuration: setAvailable ? null : (pauseDuration ?? null),
      });

      // 3. driverLocations aktualisieren
      try {
        const driverLocRef = doc(db, 'driverLocations', driverId);
        const driverLocSnap = await getDoc(driverLocRef);
        if (driverLocSnap.exists()) {
          if (setAvailable) {
            await updateDoc(driverLocRef, {
              isAvailable: true,
              freeSince: serverTimestamp(),
            });
          } else {
            await updateDoc(driverLocRef, {
              isAvailable: false,
              freeSince: deleteField(),
            });
          }
        }
      } catch (locErr) {
        console.warn('driverLocations Update fehlgeschlagen:', locErr);
      }

      return { driverId, isAvailable: setAvailable };
    } catch (error) {
      console.error('Error toggling driver availability:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Thunk zum Starten des Echtzeit-Listeners für Fahrer-Status
export const startDriverStatusListener = () => (dispatch) => {
  console.log('Starte Echtzeit-Listener für Fahrer-Status...');
  
  // Existierenden Listener entfernen, falls vorhanden
  if (driversStatusListener) {
    console.log('Beende alten Listener...');
    driversStatusListener();
    driversStatusListener = null;
  }
  
  // Spezielle Anpassungen für Produktionsumgebungen
  const PROD_ENV = process.env.NODE_ENV === 'production';
  
  // Helper für zuverlässigere Echtzeit-Updates in Produktionsumgebungen
  const enhanceProductionReliability = (callback) => {
    return (...args) => {
      // In Produktionsumgebungen zusätzliche Verzögerung für stabilere Verbindungen
      if (PROD_ENV) {
        console.log('DriversSlice: Produktionsoptimierungen werden angewendet');
        setTimeout(() => callback(...args), 500);
      } else {
        callback(...args);
      }
    };
  };
  
  // Listener für die Benutzersammlung einrichten
  const usersQuery = query(
    collection(db, 'users'),
    where('isDriver', '==', true)
  );
  
  // In Produktionsumgebungen robusteres Error-Handling
  const errorHandler = (error) => {
    console.error('Fehler im Fahrer-Status-Listener:', error);
    
    if (PROD_ENV) {
      console.log('DriversSlice: Versuche Fahrer-Listener neu zu starten...');
      // Im Produktionsmodus nach Fehlern neu starten
      setTimeout(() => {
        try {
          dispatch(startDriverStatusListener());
          console.log('DriversSlice: Listener erfolgreich neu gestartet');
        } catch (e) {
          console.error('DriversSlice: Neustart des Listeners fehlgeschlagen:', e);
        }
      }, 2000);
    }
  };
  
  // Wrapper für zuverlässigere Callbacks in Produktionsumgebungen
  const processDriverChanges = enhanceProductionReliability((snapshot) => {
    snapshot.docChanges().forEach(async (change) => {
      if (change.type !== 'added' && change.type !== 'modified') return;

      const userData = change.doc.data();
      const userId = change.doc.id;

      let updatedStatus;
      if (!userData.isOnline) {
        updatedStatus = 'offline';
      } else if (userData.isAvailable) {
        updatedStatus = 'available';
      } else {
        updatedStatus = await getRideBasedStatus(userId);
      }

      console.log(`Echtzeit-Update Fahrer ${userId}: Status = ${updatedStatus}`);

      try {
        dispatch(driversSlice.actions.updateDriverRealtime({
          id: userId,
          isOnline: userData.isOnline || false,
          isAvailable: userData.isAvailable || false,
          status: updatedStatus,
          vehicle: userData.car ? {
            id: userData.car.vehicleId,
            model: userData.car.model,
            license_plate: userData.car.licensePlate
          } : null
        }));
      } catch (error) {
        console.error(`Fehler beim Aktualisieren des Redux Store für Fahrer ${userId}:`, error);
      }
    });

    if (PROD_ENV) {
      console.log('DriversSlice: Fahrer-Updates verarbeitet',
        { timestamp: new Date().toISOString(), changes: snapshot.docChanges().length });
    }
  });
  
  driversStatusListener = onSnapshot(
    usersQuery,
    {
      includeMetadataChanges: PROD_ENV // In Produktion auch Metadaten-Updates beobachten
    },
    processDriverChanges,
    errorHandler
  );
  
  if (PROD_ENV) {
    console.log('DriversSlice: Echtzeit-Listener erfolgreich gestartet (Produktionsmodus)');
  }
  
  return () => {
    // Cleanup-Funktion zurückgeben
    if (driversStatusListener) {
      driversStatusListener();
      driversStatusListener = null;
    }
  };
};

// Thunk zum Stoppen des Echtzeit-Listeners
export const stopDriverStatusListener = () => () => {
  console.log('Stoppe Echtzeit-Listener für Fahrer-Status...');
  if (driversStatusListener) {
    driversStatusListener();
    driversStatusListener = null;
  }
};

export const { clearDriversError, clearCurrentDriver, updateDriverRealtime } = driversSlice.actions;
export default driversSlice.reducer;
